import { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";

import {
  CHECKOUT_PROVIDER_FLAG_TIMEOUT_MS,
  fetchJussubCheckoutFlag,
} from "./checkoutProviderFlag";

export { CHECKOUT_PROVIDER_FLAG_TIMEOUT_MS } from "./checkoutProviderFlag";

export const CHECKOUT_PROVIDER = Object.freeze({
  JUSSUB: "jussub",
  JUSBILL: "jusbill",
});

export const resolveCheckoutProvider = ({
  isResolved,
  isJussubEnabled,
  paymentMethod,
}) => {
  if (!isResolved) {
    const error = new Error("Modo de checkout ainda não resolvido");
    error.code = "CHECKOUT_PROVIDER_PENDING";
    error.paymentMethod = paymentMethod;
    throw error;
  }

  return isJussubEnabled
    ? CHECKOUT_PROVIDER.JUSSUB
    : CHECKOUT_PROVIDER.JUSBILL;
};

// coupom vem de state.app.coupom (payload de LOAD_COUPOM_SUCCESS). Quando o cupom já
// resolveu contra um backend (is_jussub boolean), essa resposta vale mais que a flag
// bruta — é a fonte de verdade de ONDE o cupom realmente vive (validateOnJussub tenta
// sempre primeiro). Sem is_jussub definido (sem cupom, ainda carregando, ou inválido
// nos dois backends) cai pra flag.
export const resolveEffectiveJussubEnabled = ({ isJussubEnabled, coupom }) =>
  typeof coupom?.is_jussub === "boolean" ? coupom.is_jussub : isJussubEnabled;

export const useCheckoutProviderMode = ({
  timeoutMs = CHECKOUT_PROVIDER_FLAG_TIMEOUT_MS,
} = {}) => {
  const [decision, setDecision] = useState(null);
  const reportedErrorRef = useRef(null);

  useEffect(() => {
    let active = true;
    let settled = false;

    const timeoutId = window.setTimeout(() => {
      if (!active || settled) return;
      settled = true;
      setDecision({
        provider: CHECKOUT_PROVIDER.JUSBILL,
        fallbackReason: "timeout",
        resolutionError: new Error(
          "Timeout ao resolver o provider do checkout"
        ),
      });
    }, timeoutMs);

    fetchJussubCheckoutFlag({ timeoutMs })
      .then(isJussubEnabled => {
        if (!active || settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        setDecision({
          provider: isJussubEnabled
            ? CHECKOUT_PROVIDER.JUSSUB
            : CHECKOUT_PROVIDER.JUSBILL,
          fallbackReason: null,
          resolutionError: null,
        });
      })
      .catch(error => {
        if (!active || settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        setDecision({
          provider: CHECKOUT_PROVIDER.JUSBILL,
          fallbackReason: "error",
          resolutionError: error,
        });
      });

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [timeoutMs]);

  useEffect(() => {
    if (
      !decision?.resolutionError ||
      reportedErrorRef.current === decision.resolutionError
    ) {
      return;
    }

    reportedErrorRef.current = decision.resolutionError;
    Sentry.captureException(decision.resolutionError);
    toast.warning(
      "Não foi possível carregar o checkout dinâmico. Seguiremos com o checkout padrão."
    );
  }, [decision]);

  return {
    provider: decision?.provider ?? null,
    isResolved: Boolean(decision),
    isJussubEnabled: decision?.provider === CHECKOUT_PROVIDER.JUSSUB,
    fallbackReason: decision?.fallbackReason ?? null,
    resolutionError: decision?.resolutionError ?? null,
  };
};
